import {AbstractViewModel} from '../../../core/interfaces/abstract-view-model';
import {ProjectCollaborationApiModel} from '../../../api/models/collaboration/project-collaboration.api-model';
import {ProjectViewModel} from '../garage/project.view-model';
import {ProjectApiModel} from '../../../api/models/project.api-model';
import {UserViewModel} from '../settings/user.view-model';
import {UserApiModel} from '../../../api/models/user.api-model';
import {ProjectCollaborationAssetViewModel} from './project-collaboration-asset.view-model';
import {ProjectCollaborationAssetApiModel} from '../../../api/models/collaboration/project-collaboration-asset.api-model';
import {CollaborationStatusEnum} from './collaboration-status.enum';

export class ProjectCollaborationViewModel extends AbstractViewModel<ProjectCollaborationApiModel> {
  public constructor(protected apiModel: ProjectCollaborationApiModel = new ProjectCollaborationApiModel()) {
    super(apiModel);
    this.fromApiModel(apiModel);
  }

  public project: ProjectViewModel;
  public owner: UserViewModel;
  public collaborator: UserViewModel;
  public slug: string;
  public type: string;
  public impactType: string;
  public status: CollaborationStatusEnum;
  public title: string;
  public shortDescription: string;
  public description: string;
  public serviceSummary: string;
  public reviewNoteOwner: string;
  public createHistoryEntryOnApproval: boolean;
  public historyStartDate?: Date;
  public historyEndDate?: Date;
  public acceptedAssetsCount: number;
  public pendingAssetsCount: number;
  public rejectedAssetsCount: number;
  public submittedAt: Date;
  public acceptedAt: Date;
  public rejectedAt: Date;
  public archivedAt: Date;
  public assets: ProjectCollaborationAssetViewModel[] = [];
  public coverAsset: ProjectCollaborationAssetViewModel | null = null;
  public historyPost: string | null;
  public createdAt: Date;
  public updatedAt: Date;

  private createAssetFromRef(assetRef: string | ProjectCollaborationAssetApiModel): ProjectCollaborationAssetViewModel {
    if (typeof assetRef !== 'string') {
      return new ProjectCollaborationAssetViewModel(assetRef);
    }

    const vm = new ProjectCollaborationAssetViewModel();
    vm['@id'] = assetRef;
    vm.id = Number(assetRef.split('/').pop()) || 0;
    return vm;
  }

  fromApiModel(apiModel: ProjectCollaborationApiModel): this {
    this.id = apiModel.id;
    this.slug = apiModel.slug;
    this.type = apiModel.type;
    this.impactType = apiModel.impactType;
    this.status = apiModel.status as CollaborationStatusEnum;
    this.title = apiModel.title;
    this.shortDescription = apiModel.shortDescription;
    this.description = apiModel.description;
    this.serviceSummary = apiModel.serviceSummary;
    this.reviewNoteOwner = apiModel.reviewNoteOwner;
    this.createHistoryEntryOnApproval = !!apiModel.createHistoryEntryOnApproval;
    this.historyStartDate = apiModel.historyStartDate;
    this.historyEndDate = apiModel.historyEndDate;
    this.acceptedAssetsCount = apiModel.acceptedAssetsCount || 0;
    this.pendingAssetsCount = apiModel.pendingAssetsCount || 0;
    this.rejectedAssetsCount = apiModel.rejectedAssetsCount || 0;
    this.submittedAt = apiModel.submittedAt;
    this.acceptedAt = apiModel.acceptedAt;
    this.rejectedAt = apiModel.rejectedAt;
    this.archivedAt = apiModel.archivedAt;
    this.createdAt = apiModel.createdAt;
    this.updatedAt = apiModel.updatedAt;

    this.project = typeof apiModel.project === 'string'
      ? (() => {
          const vm = new ProjectViewModel();
          vm['@id'] = apiModel.project as string;
          vm.slug = (apiModel.project as string).split('/').pop() || null;
          return vm;
        })()
      : new ProjectViewModel(apiModel.project as ProjectApiModel);

    this.owner = typeof apiModel.owner === 'string'
      ? (() => {
          const vm = new UserViewModel();
          vm['@id'] = apiModel.owner as string;
          vm.slug = (apiModel.owner as string).split('/').pop() || null;
          return vm;
        })()
      : new UserViewModel(apiModel.owner as UserApiModel);

    this.collaborator = typeof apiModel.collaborator === 'string'
      ? (() => {
          const vm = new UserViewModel();
          vm['@id'] = apiModel.collaborator as string;
          vm.slug = (apiModel.collaborator as string).split('/').pop() || null;
          return vm;
        })()
      : new UserViewModel(apiModel.collaborator as UserApiModel);

    this.assets = (apiModel.assets || [])
      .filter((asset) => !!asset)
      .map((asset) => this.createAssetFromRef(asset as string | ProjectCollaborationAssetApiModel));

    this.coverAsset = !apiModel.coverAsset
      ? null
      : this.createAssetFromRef(apiModel.coverAsset as string | ProjectCollaborationAssetApiModel);

    this.historyPost = typeof apiModel.historyPost === 'string'
      ? apiModel.historyPost
      : (apiModel.historyPost?.['@id'] || (apiModel.historyPost?.id ? `/api/history_posts/${apiModel.historyPost.id}` : null));

    return this;
  }

  getApiModel(): ProjectCollaborationApiModel {
    return this.apiModel;
  }

  toApiModel(): ProjectCollaborationApiModel {
    this.apiModel.project = this.project?.['@id'];
    this.apiModel.type = this.type;
    this.apiModel.title = this.title;
    this.apiModel.shortDescription = this.shortDescription;
    this.apiModel.description = this.description;
    this.apiModel.serviceSummary = this.serviceSummary;
    this.apiModel.createHistoryEntryOnApproval = this.createHistoryEntryOnApproval;
    this.apiModel.historyStartDate = this.historyStartDate;
    this.apiModel.historyEndDate = this.historyEndDate;

    return this.apiModel;
  }
}
